import { useCallback } from 'react';

import {
  BlotterTable,
  BlotterTableFilters,
  Button,
  FormControlSizes,
  FormattedMessage,
  MixpanelEvent,
  WSBlotterTableMaxRecordsReachedWarning,
  useMixpanel,
} from '@talos/kyoko';

import { useBlotterSettings } from '../BlotterSettingsContext';
import { ShowOnlySelfFilter } from '../ShowOnlySelfFilter';
import { BlotterActions } from '../styles';

import { defineMessages } from 'react-intl';
import { CustomHyperlinkButtons } from '../CustomHyperlinkButtons';
import type { TabbedBlotterPropsWithSelfFilter } from '../types';
import { useActiveOrdersBlotter } from './hooks/useActiveOrdersBlotter';

const messages = defineMessages({
  archiveAll: {
    id: 'Blotters.ActiveOrdersBlotter.archiveAll',
    defaultMessage: 'Archive All',
  },
});

export type ActiveOrdersBlottersProps = TabbedBlotterPropsWithSelfFilter;

export const ActiveOrdersBlotter = ({
  renderInBackground: hidden,
  loading,
  maxRows,
  onStartLoading,
  onFinishedLoading,
  suppressShowOnlySelfOrdersAndTrades = false,
}: ActiveOrdersBlottersProps) => {
  const mixpanel = useMixpanel();
  const { showOnlySelfOrdersAndTrades } = useBlotterSettings();

  const { archiveAll, blotterTable, blotterTableFilterProps, filterBuilderAccordion } = useActiveOrdersBlotter({
    maxRows,
    showOnlySelfOrdersAndTrades,
  });

  const handleArchiveAllOrders = useCallback(() => {
    onStartLoading();
    archiveAll();
    /**
     * The archiveAll function executes fairly instantaneously, so the
     * loading spinner doesn't really have a chance to display. In this
     * case, we should artificially delay the onFinishedLoading callback
     * for half a second, just to at least give the user a visual cue.
     */
    setTimeout(onFinishedLoading, 500);
    mixpanel.track(MixpanelEvent.ClearAllOrders);
  }, [archiveAll, mixpanel, onStartLoading, onFinishedLoading]);

  return (
    <>
      {!hidden && (
        <>
          <BlotterTableFilters
            {...filterBuilderAccordion}
            {...blotterTableFilterProps}
            {...blotterTable.blotterTableFiltersProps}
            prefix={
              <BlotterActions>
                <CustomHyperlinkButtons blotter="activeOrders" />
                <Button size={FormControlSizes.Small} loading={loading} onClick={handleArchiveAllOrders}>
                  <FormattedMessage {...messages.archiveAll} />
                </Button>
                {!suppressShowOnlySelfOrdersAndTrades && <ShowOnlySelfFilter type="orders" />}
              </BlotterActions>
            }
          />
        </>
      )}
      {!hidden && (
        <WSBlotterTableMaxRecordsReachedWarning {...blotterTable.paginationLimit} getTimestamp={r => r.Timestamp} />
      )}
      <BlotterTable {...blotterTable} />
    </>
  );
};
